import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";

const HistoricalTradeDataTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const [state, setState] = useState({
    trades: [],
    pageSize: 15,
    currentPage: 0,
    totalCount: 0,
    isLoading: true,
  });

  const {
    product,
    marketDate,
    priceBasisId,
    currencyId,
    uomId,
    organizaitonId,
    currencySymbol,
    pricePrecision,
    volumePrecision,
    onRowClick,
  } = props;

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      type: "centerAligned",
      minWidth: 140,
      wrapText: false,
      autoHeight: false,
      wrapHeaderText: false,
      autoHeaderHeight: false,
    }),
    []
  );

  // Map to view model
  const mapToViewModel = useCallback(
    (trades) => {
      if (!trades) return [];

      const dateFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "numeric",
      };

      return trades.map((trade) => ({
        ...{ shortName: product.shortName },
        ...trade,
        ...{
          tradeDateFormatted: new Date(trade.tradeDate).toLocaleDateString(
            "en-US",
            dateFormatOptions
          ),
          priceFormatted:
            currencySymbol + Number(trade.price).toFixed(pricePrecision),
          volumeFormatted: Number(trade.volume).toFixed(volumePrecision),
        },
        ...trade.qualityAttributeValues.reduce((acc, attribute) => {
          acc[attribute.qualityAttributeId] = attribute.value;
          return acc;
        }, {}),
      }));
    },
    [product, currencySymbol, pricePrecision, volumePrecision]
  );

  // Memoize column definitions
  const columnDefs = useMemo(() => {
    if (!product) return [];

    return [
      { field: "shortName", headerName: t("MarketDataProduct"), flex: 1 },
      ...product.qualityAttributes
        .filter((qualityAttribute) => qualityAttribute.isPriceDriver !== false)
        .map((qualityAttribute) => ({
          field: qualityAttribute.qualityAttributeId,
          headerName: qualityAttribute.qualityAttributeName,
          flex: 1,
        })),
      { field: "termName", headerName: t("Term"), flex: 1 },
      {
        field: "buyerOrganizationName",
        headerName: t("BuyerOrganization"),
        flex: 1,
      },
      {
        field: "sellerOrganizationName",
        headerName: t("SellerOrganization"),
        flex: 1,
      },
      { field: "priceBasisName", headerName: t("PriceBasis"), flex: 1 },
      { field: "priceFormatted", headerName: t("Price"), flex: 1 },
      { field: "volumeFormatted", headerName: t("Volume"), flex: 1 },
      {
        field: "tradeDateFormatted",
        headerName: t("TradeDate"),
        flex: 1,
        sort: "desc",
        comparator: (valueA, valueB) => {
          if (valueA == null && valueB == null) return 0;
          if (valueA == null) return -1;
          if (valueB == null) return 1;
          return new Date(valueA) - new Date(valueB);
        },
      },
    ];
  }, [product, t]);

  // Create a getRowId function to ensure stable row identification
  const getRowId = useCallback((params) => params.data.tradeId.toString(), []);

  // Load trades data
  useEffect(() => {
    const loadTradesData = async () => {
      if (!product) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true }));

      try {
        // Simulate loading historical trades - in a real implementation this would come from props or API
        // Replace with actual data fetching mechanism
        const simulatedDelay = setTimeout(() => {
          const historicalTrades = props.trades || [];
          const mappedTrades = mapToViewModel(historicalTrades);

          setState((prev) => ({
            ...prev,
            trades: mappedTrades,
            totalCount: mappedTrades.length,
            isLoading: false,
          }));
        }, 500);

        return () => clearTimeout(simulatedDelay);
      } catch (error) {
        console.error("Error loading trades", error);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    loadTradesData();
  }, [
    product,
    props.trades,
    marketDate,
    priceBasisId,
    currencyId,
    uomId,
    organizaitonId,
    mapToViewModel,
  ]);

  // Handle row click
  const handleRowClick = useCallback(
    (params) => {
      if (onRowClick) {
        onRowClick(params.data);
      }
    },
    [onRowClick]
  );

  const autoSizeGridColumns = useCallback((params) => {
    params?.columnApi?.autoSizeAllColumns?.(false);
    params?.api?.autoSizeAllColumns?.(false);
  }, []);

  if (state.isLoading) return <Loading />;

  return (
    <div className="historical-trade-data-table">
      <ThemeableAgGrid
        className="table-historical-trades"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoSizeStrategy={{
          type: "fitCellContents",
          skipHeader: false,
          defaultMinWidth: 140,
        }}
        rowData={state.trades}
        onRowClicked={onRowClick ? handleRowClick : undefined}
        onFirstDataRendered={autoSizeGridColumns}
        onRowDataUpdated={autoSizeGridColumns}
        onGridSizeChanged={autoSizeGridColumns}
        getRowId={getRowId}
        animateRows={true}
        domLayout="autoHeight"
        allowHorizontalScroll={true}
      />
    </div>
  );
};

export default React.memo(HistoricalTradeDataTable);
